<script setup lang="ts">
	import { computed, ref, toRefs } from 'vue'

	import { useAuth } from '@/composables/useAuth'

	import { Company } from '@/modules/companies/interfaces/company'

	interface Props {
		params: Record<string, any>
		selectedCompany: Company | undefined
		selectedSupervisor?: Record<string, any> | undefined
		roles: any[]
		groups: any[]
		departments: any[]
	}

	interface Emit {
		(e: 'resetFilters'): void
		(e: 'searchParams'): void
		(e: 'handleSelectedCompany', value: Company | undefined): void
		(e: 'handleSelectedRole', value: string | undefined): void
		(e: 'handleSelectedGroup', value: number | undefined): void
		(e: 'handleSelectedDepartment', value: number | undefined): void
		(e: 'handleSelectedSupervisor', value: undefined): void
	}

	const props = defineProps<Props>()

	const emits = defineEmits<Emit>()

	const { params, selectedCompany, roles, groups, departments } = toRefs(props)
	const { user } = useAuth()

	// Estado para controlar la vista expandida/colapsada
	const isExpanded = ref(false)
	const showAllFilters = ref(false)

	const filterDisplayNames: Record<string, string> = {
		search: 'Búsqueda',
		company: 'Empresa',
		status: 'Estatus',
		role: 'Rol',
		group: 'Grupo',
		department: 'Departamento',
		supervisor: 'Supervisado por',
		sortBy: 'Ordenar por',
		sortDirection: 'Dirección',
	}

	const getFilterIcon = (reference: string) => {
		const iconMap: Record<string, string> = {
			search: 'search',
			company: 'business',
			status: 'task_alt',
			role: 'badge',
			group: 'group',
			department: 'apartment',
			supervisor: 'supervisor_account',
			sortBy: 'sort',
			sortDirection: 'swap_vert',
		}
		return iconMap[reference] || 'label'
	}

	const getFilterColor = (reference: string) => {
		const colorMap: Record<string, string> = {
			search: 'border-blue-200 bg-blue-50 text-blue-700',
			company: 'border-orange-200 bg-orange-50 text-orange-700',
			status: 'border-green-200 bg-green-50 text-green-700',
			role: 'border-purple-200 bg-purple-50 text-purple-700',
			group: 'border-indigo-200 bg-indigo-50 text-indigo-700',
			department: 'border-teal-200 bg-teal-50 text-teal-700',
			supervisor: 'border-pink-200 bg-pink-50 text-pink-700',
			sortBy: 'border-gray-200 bg-gray-50 text-gray-700',
			sortDirection: 'border-cyan-200 bg-cyan-50 text-cyan-700',
		}
		return colorMap[reference] || 'border-gray-200 bg-gray-50 text-gray-700'
	}

	// Función para obtener el valor formateado del parámetro
	const getFormattedValue = (key: string, value: any) => {
		if (value === null || value === undefined || value === '') return null

		if (key === 'status') {
			if (value === 'active') return 'Activo'
			if (value === 'inactive') return 'Inactivo'
			if (value === 'all') return 'Todos'
			return value
		}

		if (key === 'role') {
			const role = roles.value?.find((r) => r.name === value || r.id === value)
			return role?.name || value
		}

		if (key === 'group') {
			const group = groups.value?.find((g) => g.id === value)
			return group?.name || value
		}

		if (key === 'department') {
			const department = departments.value?.find((d) => d.id === value)
			return department?.name || value
		}

		if (key === 'supervisor') {
			const sup = props.selectedSupervisor
			return sup ? `${sup.name} ${sup.lastname}` : value
		}

		if (key === 'sortBy') {
			const sortByMap: Record<string, string> = {
				name: 'Nombre',
				email: 'Email',
				created_at: 'Fecha de creación',
				all: 'Todos',
			}
			return sortByMap[value] || value
		}

		if (key === 'sortDirection') {
			if (value === 'asc') return 'Ascendente'
			if (value === 'desc') return 'Descendente'
			return value
		}

		if (key === 'company') {
			return props.selectedCompany?.name
		}

		return String(value)
	}

	// Generar filtros activos directamente desde params
	const filtersFromParams = computed(() => {
		return Object.entries(params.value)
			.map(([key, value]) => {
				const formattedValue = getFormattedValue(key, value)
				if (!formattedValue) return null

				return {
					reference: key,
					key: filterDisplayNames[key] || key,
					value: formattedValue,
				}
			})
			.filter((filter) => filter !== null)
			.filter((filter) => {
				// Filtrar valores por defecto que no queremos mostrar
				if (filter!.reference === 'status' && filter!.value === 'Activo')
					return false
				if (filter!.reference === 'sortBy' && filter!.value === 'Nombre')
					return false
				if (
					filter!.reference === 'sortDirection' &&
					filter!.value === 'Ascendente'
				)
					return false
				return true
			}) as Array<{ reference: string; key: string; value: string }>
	})

	// Función para remover un filtro individual
	const removeFilter = (reference: string) => {
		if (reference === 'status') {
			params.value[reference] = 'active'
		} else if (reference === 'sortBy') {
			params.value[reference] = 'name'
		} else if (reference === 'sortDirection') {
			params.value[reference] = 'asc'
		} else if (reference === 'company') {
			params.value[reference] = undefined
			emits('handleSelectedCompany', undefined)
		} else if (reference === 'search') {
			params.value[reference] = ''
		} else if (reference === 'role') {
			params.value[reference] = null
			emits('handleSelectedRole', undefined)
		} else if (reference === 'group') {
			params.value[reference] = null
			emits('handleSelectedGroup', undefined)
		} else if (reference === 'department') {
			params.value[reference] = null
			emits('handleSelectedDepartment', undefined)
		} else if (reference === 'supervisor') {
			params.value[reference] = null
			emits('handleSelectedSupervisor', undefined)
		}
		emits('searchParams')
	}

	const activeFiltersCount = computed(() => {
		return filtersFromParams.value.length
	})

	const FILTERS_LIMIT = 4

	const visibleFilters = computed(() => {
		if (
			showAllFilters.value ||
			filtersFromParams.value.length <= FILTERS_LIMIT
		) {
			return filtersFromParams.value
		}
		return filtersFromParams.value.slice(0, FILTERS_LIMIT)
	})

	const hasHiddenFilters = computed(() => {
		return filtersFromParams.value.length > FILTERS_LIMIT
	})

	const hiddenFiltersCount = computed(() => {
		return Math.max(0, filtersFromParams.value.length - FILTERS_LIMIT)
	})

	const isCompactView = computed(() => {
		return filtersFromParams.value.length > 3 && !isExpanded.value
	})

	const toggleExpanded = () => {
		isExpanded.value = !isExpanded.value
	}

	const toggleShowAll = () => {
		showAllFilters.value = !showAllFilters.value
	}
</script>

<template>
	<div v-if="filtersFromParams.length > 0">
		<!-- Header mejorado con controles de vista -->
		<div class="px-4 py-3">
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<div class="flex items-center gap-2">
						<span class="material-symbols-outlined text-blue-600 text-base"
							>filter_alt</span
						>
						<h3 class="text-sm font-semibold text-gray-800">
							Filtros aplicados
						</h3>
						<span
							class="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-blue-500 rounded-full"
						>
							{{ activeFiltersCount }}
						</span>
					</div>

					<!-- Botón para expandir/colapsar cuando hay muchos filtros -->
					<button
						v-if="isCompactView || isExpanded"
						@click.prevent="toggleExpanded"
						class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
					>
						<span class="material-symbols-outlined text-sm">
							{{ isExpanded ? 'expand_less' : 'expand_more' }}
						</span>
						{{ isExpanded ? 'Compacto' : 'Expandir' }}
					</button>
				</div>
			</div>
		</div>

		<!-- Vista Compacta - Solo resumen -->
		<div
			v-if="isCompactView"
			class="px-4 py-3 border-b"
		>
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-2">
					<span class="text-sm text-gray-600">
						{{ activeFiltersCount }} filtros aplicados
					</span>
					<div class="flex -space-x-1">
						<div
							v-for="filter in visibleFilters.slice(0, 3)"
							:key="filter.reference"
							:class="[
								'inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-white text-xs font-bold',
								getFilterColor(filter.reference),
							]"
							:title="filter.key + ': ' + filter.value"
						>
							<span class="material-symbols-outlined text-xs opacity-80">
								{{ getFilterIcon(filter.reference) }}
							</span>
						</div>
						<div
							v-if="activeFiltersCount > 3"
							class="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-gray-100 text-gray-600 text-xs font-bold"
							:title="`+${activeFiltersCount - 3} filtros más`"
						>
							+{{ activeFiltersCount - 3 }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Vista Expandida - Lista completa de filtros -->
		<div v-else>
			<!-- Mostrar filtros limitados o todos -->
			<div class="space-y-3">
				<div class="flex flex-wrap gap-2">
					<div
						v-for="filter in visibleFilters"
						:key="filter.reference"
						:class="[
							'inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg text-xs font-medium border shadow-sm hover:shadow-md transition-all duration-200',
							getFilterColor(filter.reference),
						]"
					>
						<!-- Icono del filtro -->
						<span class="material-symbols-outlined text-sm opacity-80">
							{{ getFilterIcon(filter.reference) }}
						</span>

						<!-- Contenido del filtro -->
						<div class="flex items-center gap-1">
							<span class="font-semibold">{{ filter.key }}:</span>
							<span
								class="font-normal max-w-28 truncate"
								:title="filter.value"
							>
								{{ filter.value }}
							</span>
						</div>

						<!-- Botón para eliminar filtro individual -->
						<button
							v-if="filter.reference !== 'company' || user?.internal"
							@click.prevent="removeFilter(filter.reference)"
							class="ml-0.5 p-0.5 rounded-full hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
							:title="`Eliminar filtro: ${filter.key}`"
						>
							<span class="material-symbols-outlined text-sm text-gray-500">
								close
							</span>
						</button>

						<!-- Indicador de filtro fijo para company (solo para externos) -->
						<span
							v-else
							class="material-symbols-outlined text-sm text-gray-400"
							title="Filtro fijo"
						>
							lock
						</span>
					</div>
				</div>

				<!-- Botón "Ver más" / "Ver menos" -->
				<div
					v-if="hasHiddenFilters"
					class="flex justify-center pt-2"
				>
					<button
						@click.prevent="toggleShowAll"
						class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
					>
						<span class="material-symbols-outlined text-sm">
							{{ showAllFilters ? 'expand_less' : 'expand_more' }}
						</span>
						{{ showAllFilters ? 'Ver menos' : `Ver ${hiddenFiltersCount} más` }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
